import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token and working location to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('clinic_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Add working location header for inventory context enforcement
  const workingLocationStr = localStorage.getItem('clinic_working_location');
  if (workingLocationStr) {
    try {
      const workingLocation = JSON.parse(workingLocationStr);
      if (workingLocation?.id) {
        config.headers['X-Working-Location'] = workingLocation.id;
      }
    } catch (e) {
      // If it's just an ID string, use it directly
      config.headers['X-Working-Location'] = workingLocationStr;
    }
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('clinic_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  refreshToken: () => api.post('/auth/refresh-token'),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  impersonate: (userId) => api.post(`/auth/impersonate/${userId}`)
};

// Users
export const usersAPI = {
  getAll: (params = {}) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id) => api.post(`/users/${id}/reset-provisional-password`)
};

// Locations
export const locationsAPI = {
  getAll: (params = {}) => api.get('/locations', { params }),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`)
};

// Patients
export const patientsAPI = {
  getAll: (params = {}) => api.get('/patients', { params }),
  getById: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  getActiveVisits: () => api.get('/patients/active-visits'),
  getActiveVisit: (id) => api.get(`/patients/${id}/active-visit`)
};

// Services
export const servicesAPI = {
  getAll: (params = {}) => api.get('/services', { params }),
  getById: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`)
};

// Inventory
export const inventoryAPI = {
  getAll: (params = {}) => api.get('/inventory', { params }),
  getById: (id) => api.get(`/inventory/${id}`),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  add: (data) => api.post('/inventory/add', data),
  adjust: (data) => api.post('/inventory/adjust', data),
  consume: (data) => api.post('/inventory/consume', data),
  transfer: (data) => api.post('/inventory/transfer', data),
  getTransactions: (params) => api.get('/inventory/transactions', { params }),
  getAuditLogs: (params) => api.get('/inventory/audit-logs', { params }),
  getExpirationAlerts: (days = 90, locationId = null) => api.get('/inventory/expiration-alerts', { params: { days_threshold: days, ...(locationId && { location_id: locationId }) } }),
  // Batch management endpoints
  getBatches: (itemId, includeDepleted = false) => api.get(`/inventory/${itemId}/batches`, { params: { include_depleted: includeDepleted } }),
  addBatch: (itemId, data) => api.post(`/inventory/${itemId}/batches`, data),
  updateBatch: (itemId, batchId, data) => api.patch(`/inventory/${itemId}/batches/${batchId}`, data),
  deleteBatch: (itemId, batchId) => api.delete(`/inventory/${itemId}/batches/${batchId}`),
  uploadBatchInvoice: (itemId, batchId, formData) => api.post(`/inventory/${itemId}/batches/${batchId}/upload-invoice`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  bulkImportBatches: (data) => api.post('/inventory/bulk-import-batches', data)
};

// Visits
export const visitsAPI = {
  getAll: (params = {}) => api.get('/visits', { params }),
  getById: (id) => api.get(`/visits/${id}`),
  create: (data) => api.post('/visits', data),
  update: (id, data) => api.put(`/visits/${id}`, data),
  assignDoctor: (visitId, doctorId) => api.post(`/visits/${visitId}/assign-doctor?doctor_id=${doctorId}`),
  cancelVisit: (visitId, reason) => api.post(`/visits/${visitId}/cancel`, { reason }),
  submitForBilling: (id) => api.post(`/visits/${id}/submit-for-billing`)
};

// Bills
export const billsAPI = {
  getAll: (params = {}) => api.get('/bills', { params }),
  getByVisitIds: (visitIds) => api.post('/bills/by-visits', { visit_ids: visitIds }),
  getById: (id) => api.get(`/bills/${id}`),
  create: (data) => api.post('/bills', data),
  update: (id, data) => api.put(`/bills/${id}`, data),
  pay: (id, data) => api.post(`/bills/${id}/pay`, data),
  close: (id) => api.post(`/bills/${id}/close`),
  doctorRevise: (id, data) => api.put(`/bills/${id}/doctor-revise`, data),
  updatePaymentMethod: (id, index, method) => api.patch(`/bills/${id}/payments/${index}/method`, { method }),
  getByDoctor: (doctorId, status, locationId) => api.get(`/bills/doctor/${doctorId}`, { params: { ...(status ? { status } : {}), ...(locationId ? { location_id: locationId } : {}) } })
};

// Clinics
export const clinicsAPI = {
  getAll: (params = {}) => api.get('/clinics', { params }),
  getById: (id) => api.get(`/clinics/${id}`),
  create: (data) => api.post('/clinics', data),
  update: (id, data) => api.put(`/clinics/${id}`, data),
  delete: (id) => api.delete(`/clinics/${id}`)
};

// Doctors
export const doctorsAPI = {
  getAll: (locationId) => api.get('/doctors', { params: locationId ? { location_id: locationId } : {} })
};

// Dashboard
export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats', { params })
};

// Reports
export const reportsAPI = {
  getRevenue: (params) => api.get('/reports/revenue', { params }),
  getInventory: (params) => api.get('/reports/inventory', { params }),
  getDoctors: (params) => api.get('/reports/doctors', { params }),
  getOutstanding: (params) => api.get('/reports/outstanding', { params }),
  getDailyClosing: (date, locationId) => api.get('/reports/daily-closing', { 
    params: { date, location_id: locationId } 
  }),
  getMedicationUsage: (params) => api.get('/reports/medication-usage', { params }),
  getMedicationBillHistory: (inventoryId, params) => api.get(`/reports/medication-usage/${inventoryId}/history`, { params }),
  exportMedicationUsage: (params) => api.get('/reports/medication-usage/export', { params, responseType: 'blob' }),
  getBonusJotform: (period = 'current') => api.get('/reports/bonus-jotform', { params: { period } }),
  getConsumptionMatrix: (params) => api.get('/reports/consumption-matrix', { params }),
  getClosingRevision: (date, locationId) => api.get('/reports/daily-closing/revision', { params: { date, location_id: locationId } }),
  getClosingEmployees: () => api.get('/reports/closing-employees'),
  saveClosingRevision: (payload) => api.post('/reports/daily-closing/revision', payload),
  reopenClosingRevision: (payload) => api.post('/reports/daily-closing/revision/reopen', payload),
  getClosingsHistory: (params) => api.get('/reports/closings-history', { params }),
  getEmployeeBonuses: (params) => api.get('/reports/employee-bonuses', { params }),
  downloadPayslip: (params) => api.get('/reports/employee-bonuses/payslip', { params, responseType: 'blob' }),
  downloadAllPayslips: (params) => api.get('/reports/employee-bonuses/payslip-all', { params, responseType: 'blob' }),
  emailPayslips: (payload) => api.post('/reports/employee-bonuses/payslip/email', payload),
  uploadClosingFile: (formData) => api.post('/reports/daily-closing/upload-file', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getClosingAttachmentUrl: (date, locationId) => api.get('/reports/daily-closing/attachment-url', { params: { date, location_id: locationId } }),
  deleteClosingAttachment: (date, locationId) => api.delete('/reports/daily-closing/attachment', { params: { date, location_id: locationId } }),
  getOnShiftToday: (date, locationId) => api.get('/shifts/today', { params: { date, location_id: locationId } }),
  setOnShiftToday: (payload) => api.put('/shifts/today', payload)
};

/**
 * Filter locations based on user's assigned location_ids.
 * If user has no location_ids assigned (null/empty), returns all locations.
 * If user has specific location_ids, returns only those locations.
 * @param {Array} locations - Array of location objects
 * @param {Object} user - User object with location_ids array
 * @returns {Array} Filtered locations
 */
export const filterUserLocations = (locations, user) => {
  if (!locations || !Array.isArray(locations)) return [];
  
  const userLocationIds = user?.location_ids;
  
  // If user has no assigned locations, show all locations
  if (!userLocationIds || userLocationIds.length === 0) {
    return locations;
  }
  
  // Filter to only show user's assigned locations
  return locations.filter(loc => userLocationIds.includes(loc.id));
};

// Patient Intake (native form submissions)
export const intakeAPI = {
  getSubmissions: (params = {}) => api.get('/patient-intake/submissions', { params }),
  getSubmission: (id) => api.get(`/patient-intake/submissions/${id}`),
  getPhoto: (id, slug) => api.get(`/patient-intake/submissions/${id}/photo/${slug}`, { responseType: 'blob' }),
  resend: (id) => api.post(`/patient-intake/submissions/${id}/resend`),
};

export const employeesAPI = {
  getAll: (params = {}) => api.get('/employees', { params }),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
};

export default api;
